<script setup lang="ts">
import {ref, computed, defineModel, onBeforeUnmount, watch, nextTick} from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import IconChevronBottom from "@/assets/icons/IconChevronBottom.vue";
import IconChevronRight from "@/assets/icons/IconChevronRight.vue";
import {districtList} from "@/composables/list/district.list";
import {getRegionsByDistrict} from "@/composables/functions/getRegionByDistrict";
import {getDistrictName, type DistrictKey} from "@/composables/functions/getDistrictName";
import {getRegionName, type RegionKey} from "@/composables/functions/getRegionName";
import {citiesList} from "@/composables/list/cities.list";
import {regionToDistrictList} from "@/composables/list/regionToDistrict.list";

const isDistrictListOpen = ref<boolean>(false);
const hoveredDistrictKey = ref<DistrictKey | null>(null);
const hoveredDistrictElement = ref<HTMLElement | null>(null);
const districtSelectorRef = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
let ps: PerfectScrollbar | null = null;

// Значение поисковой строки и список регионов для отображения.
const searchStringQuery = ref<string>("");
const regionListWithSearchQuery = computed(() => {
  const query = searchStringQuery.value.toLowerCase();
  return Object.fromEntries(
      Object.entries(citiesList).filter(([_key, regionName]) =>
          regionName.toLowerCase().includes(query)
      )
  );
});


const selectedDistrictKey = defineModel<DistrictKey | null>("selectedDistrict", {required: true});
const selectedRegionKey = defineModel<RegionKey | null>("selectedRegion", {required: true});

const displayedDistrictName = computed<string>(() => {
  return getDistrictName(selectedDistrictKey.value);
});

const displayedDistrictKey = computed<string>(() => {
  return selectedDistrictKey.value ?? "all";
});

const hoveredDistrictRegions = computed(() => {
  if (!hoveredDistrictKey.value) return {};
  return getRegionsByDistrict(hoveredDistrictKey.value);
});

const hasRegionsToShow = computed<boolean>(() => {
  return hoveredDistrictKey.value !== null && Object.keys(hoveredDistrictRegions.value).length > 0;
});

// Вычисляем позицию списка регионов относительно наведенного элемента
const regionSelectorStyle = computed(() => {
  if (!hoveredDistrictElement.value || !districtSelectorRef.value) return { top: "0px" };

  const elementRect = hoveredDistrictElement.value.getBoundingClientRect();
  const selectorRect = districtSelectorRef.value.getBoundingClientRect();

  // Вычисляем позицию относительно district-selector
  const topPosition = elementRect.top - selectorRect.top;

  return {
    top: `${topPosition}px`
  };
});

const toggleDistrictList = (): void => {
  isDistrictListOpen.value = !isDistrictListOpen.value;
  if (!isDistrictListOpen.value) {
    hoveredDistrictKey.value = null;
    hoveredDistrictElement.value = null;
  }
};

const handleDistrictHover = (districtKey: DistrictKey, event: Event): void => {
  hoveredDistrictKey.value = districtKey;
  hoveredDistrictElement.value = event.currentTarget as HTMLElement;
};

const handleDistrictLeave = (): void => {
  // Логика при необходимости
};

const selectDistrict = (districtKey: DistrictKey): void => {
  selectedDistrictKey.value = districtKey;
  selectedRegionKey.value = null;
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const selectAll = (): void => {
  selectedDistrictKey.value = null;
  selectedRegionKey.value = null;
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const selectRegion = (regionKey: RegionKey): void => {
  selectedRegionKey.value = regionKey;
  selectedDistrictKey.value = hoveredDistrictKey.value;
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const handleClickOutside = (event?: MouseEvent): void => {
  if (event?.target && districtSelectorRef.value) {
    const target = event.target as Node;

    // Проверяем, был ли клик внутри выпадающего списка. (Очистка поисковой строки)
    if (districtSelectorRef.value.contains(target)) {
      return;
    }
  }

  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

const selectRegionWithDistrict = (regionKey: RegionKey): void => {
  selectedRegionKey.value = regionKey;
  selectedDistrictKey.value = regionToDistrictList[regionKey];
  isDistrictListOpen.value = false;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

// Поиск региона по поисковому запросу
const searchRegion = (reginName: string): void => {
  searchStringQuery.value = reginName;
  hoveredDistrictKey.value = null;
  hoveredDistrictElement.value = null;
};

// Perfect Scrollbar
const needsScroll = computed(() => {
  return Object.keys(regionListWithSearchQuery.value).length > 8; // порог
});

onBeforeUnmount(() => {
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

// Обновляем и инициализируем скролл при изменении
watch([searchStringQuery, isDistrictListOpen, needsScroll], async () => {
  await nextTick();

  // Если список открыт и контейнер доступен
  if (isDistrictListOpen.value && scrollContainer.value) {
    // Инициализируем, если еще не инициализирован
    if (!ps) {
      ps = new PerfectScrollbar(scrollContainer.value, {
        wheelSpeed: 1,
        wheelPropagation: false,
        minScrollbarLength: 20
      });
    } else {
      // Обновляем существующий
      ps.update();
    }
  }
}, { immediate: true });
</script>

<template>
  <div
    v-click-outside="handleClickOutside"
    class="wrapper"
  >
    <div
      class="district-view"
      :class="{'district-view--region': selectedRegionKey}"
      @click="toggleDistrictList"
    >
      <span class="district-view__title">
        {{ selectedRegionKey ? getDistrictName(selectedDistrictKey) : 'Федеральный округ' }}
      </span>
      <span class="district-view__name">
        {{ selectedRegionKey ? getRegionName(selectedRegionKey) : displayedDistrictName }}
      </span>
      <IconChevronBottom
        class="selector__icon"
        :class="{'selector__icon--active': isDistrictListOpen}"
      />
    </div>

    <div
      v-if="isDistrictListOpen"
      ref="districtSelectorRef"
      class="district-selector"
    >
      <div
        @mousedown.stop
        @mouseup.stop
        @click.stop
      >
        <InputSearchSecondary
          :model-value="searchStringQuery"
          placeholder="Поиск"
          @update:model-value="searchRegion"
        />
      </div>

      <!--   По умолчанию отображается список округов.   -->

      <ListView
        v-if="searchStringQuery.length === 0"
        class="district-selector__list"
      >
        <ListItem
          item="all"
          :model-value="displayedDistrictKey"
          class="district-selector__item"
          @update:model-value="selectAll"
        >
          Все
        </ListItem>
        <ListItem
          v-for="(districtName, districtKey) in districtList"
          :key="districtKey"
          :item="districtKey"
          :model-value="displayedDistrictKey"
          class="district-selector__item"
          @mouseenter="handleDistrictHover(districtKey as DistrictKey, $event)"
          @mouseleave="handleDistrictLeave"
          @click="selectDistrict(districtKey as DistrictKey)"
        >
          {{ districtName.short }}
          <IconChevronRight class="district-view__button-collapse" />
        </ListItem>
      </ListView>

      <!--   Список регионов при вводе значения в поисковую строку   -->

      <div
        v-else
        ref="scrollContainer"
        class="district-selector__scrollable"
        :class="{'district-selector__scrollable--limited': needsScroll}"
      >
        <ListView>
          <ListItem
            v-for="(regionName, regionKey) in regionListWithSearchQuery"
            :key="regionKey"
            :item="regionKey"
            :model-value="selectedRegionKey"
            class="region-selector__item"
            @update:model-value="selectRegionWithDistrict($event as RegionKey)"
          >
            {{ regionName }}
          </ListItem>
        </ListView>
      </div>

      <!--   Список регионов отображаемых при наведение на округ.   -->
      <div
        v-if="hasRegionsToShow"
        class="region-selector"
        :style="regionSelectorStyle"
      >
        <ListView>
          <ListItem
            v-for="(regionName, regionKey) in hoveredDistrictRegions"
            :key="regionKey"
            :item="regionKey"
            :model-value="selectedRegionKey"
            class="region-selector__item"
            @update:model-value="selectRegion($event as RegionKey)"
          >
            {{ regionName }}
          </ListItem>
        </ListView>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

.wrapper {
  position: relative;
}

.region-selector {
  position: absolute;
  left: calc(100% + 5px);
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  background-color: var(--background-bg-white);
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
}

.district-selector {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  border-radius: 8px;
  background-color: var(--background-bg-white);
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  position: absolute;
  width: 100%;
  top: calc(100%);
  z-index: 10;

  &__scrollable {
    overflow: hidden;
    position: relative;

    &--limited {
      height: 320px;
      padding-right: 14px;
    }
  }

  &__item {
    display: flex;
    justify-content: space-between;
  }
}

// Кастомизация perfect-scrollbar
:deep(.ps__rail-y) {
  opacity: 1 !important;
  width: 14px !important;
  right: 0;
}

:deep(.ps__thumb-y) {
  background-color: #1A64D433 !important;
  border-radius: 30px !important;
  width: 7px !important;
  right: 5px !important;
}

.district-view {
  display: flex;
  align-items: center;
  width: 220px;
  height: 36px;
  box-shadow: 1px 2px 10px 0 var(--gray-gray50);
  background-color: var(--background-bg-white);
  padding: 0 12px;
  border-radius: 8px;
  justify-content: space-between;
  //gap: 10px;

  &--region {
    gap: 10px;
  }

  &__title {
    font-weight: 500;
    font-size: 14px;
    line-height: 18px;
  }

  &__name {
    font-weight: 400;
    font-size: 14px;
    color: var(--text-text-pur);
    line-height: 18px;
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__button-collapse {
    cursor: pointer;
    color: var(--icon-icon-secondary);

    min-width: 14px;
    min-height: 14px;
  }
}

.selector__icon {
  color: var(--icon-icon-secondary);
  cursor: pointer;
  user-select: none;
  transition: transform .2s;
  width: 14px;
  height: 14px;

  &--active {
    color: var(--icon-icon-on);
    transform: rotate(180deg);
  }
}

</style>